/**
 * asset_type_fields_controller.js — the asset builder's row list (Stimulus).
 *
 * Add a check, move one up or down, retire one. Everything it does is to the
 * DOM; the form posts, the server decides. There is no JS validation here on
 * purpose: application/schemas/asset_fields.py is the one validator, and a
 * second copy of "what makes a definition legal" in the browser would drift.
 *
 * Order is the DOM order. Each row carries a hidden `field_row` marker and the
 * server reads those markers in the order the browser sends them, so moving a
 * row is a node swap and nothing renumbers. A move handler cannot put the saved
 * order out of step with what the person is looking at, because there is no
 * second representation of the order to get wrong.
 *
 * A row added here is named only once this controller names it. The <template>
 * ships its inputs as data-new-* with NO name attribute, so if this file fails
 * to load the row simply posts nothing — the worst case is a check that was not
 * added, never a half-formed definition reaching update_fields.
 *
 * Rows sit in two sections, one per Recorded setting. The setting is what
 * posts; the section only shows it, so changing the setting moves the row
 * across (rescope) and a move up or down stays inside its own section.
 *
 * The previews beside the sections are the server's: every edit posts the
 * unsaved form to the preview route, which reads it through the same form as a
 * save and answers one Turbo Stream that redraws both previews through the
 * same macro as the record sheet, so the browser never draws a field itself.
 * Edits are batched for a moment; an answer that arrives after a newer request
 * was sent is dropped.
 *
 * Unsaved edits are visible and guarded: the marker beside Close reads
 * "Unsaved changes" from the first edit (_save_status.js), Close asks before
 * dropping them, and the page-wide registry (_unsaved_work.js) asks before a
 * navigation would. A save repaints the whole editor, whose fresh render says
 * how the save went (the state value), so this controller never has to.
 *
 * Markup contract inside the root:
 *   [data-asset-type-fields-target="rows"][data-scope]  one section's rows, per scope
 *   [data-asset-type-fields-target="template"]  a <template> holding one blank row
 *   [data-asset-type-fields-target="status"]    where the save marker renders
 *   [data-field-row]                            each row, so move finds its bounds
 * The add action takes the section's scope as data-asset-type-fields-scope-param.
 * Values: previewUrl (the preview route), state ('saved' or 'failed' just
 * after a save, otherwise empty).
 */

import { Controller } from "@hotwired/stimulus";
import { clearSaveStatus, saveStatusFor, showSaveStatus } from "./_save_status.js";
import { guardUnsavedWork, releaseUnsavedWork, unsavedWorkChanged } from "./_unsaved_work.js";

// data-new-* attribute -> the form field it becomes, once the row has an id.
const NEW_ROW_INPUTS = {
    'data-new-label': 'field_label',
    'data-new-type': 'field_type',
    'data-new-unit': 'field_unit',
    'data-new-scope': 'field_scope',
    'data-new-choices': 'field_choices',
    'data-new-required-hidden': 'field_required',
    'data-new-required': 'field_required',
};

// The two rows sliding past each other when one moves: short, so a run of
// clicks still feels immediate. Skipped for anyone who has asked for less motion.
const MOVE_ANIMATION = { id: 'field-row-move', duration: 200, easing: 'ease-out' };
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

// Long enough to batch a word being typed into one request, short enough that
// the preview reads as following the edit.
const PREVIEW_DELAY_MS = 250;
const CLOSE_PROMPT = 'You have unsaved changes. Close without saving?';

export default class extends Controller {
    static targets = ['rows', 'template', 'status'];
    static values = { previewUrl: String, state: String };

    connect() {
        this._added = 0;
        this._dirty = false;
        this._previewRequest = 0;
        this._marker = saveStatusFor(this.element, this.statusTarget);
        if (this.stateValue) showSaveStatus(this._marker, this.stateValue);

        this._onEdit = () => this._edited();
        this._onSubmit = () => showSaveStatus(this._marker, 'saving');
        this.element.addEventListener('input', this._onEdit);
        this.element.addEventListener('change', this._onEdit);
        this.element.addEventListener('submit', this._onSubmit);
        guardUnsavedWork(this.element, () => this._dirty);
    }

    disconnect() {
        clearTimeout(this._previewTimer);
        clearSaveStatus(this._marker);
        this.element.removeEventListener('input', this._onEdit);
        this.element.removeEventListener('change', this._onEdit);
        this.element.removeEventListener('submit', this._onSubmit);
        releaseUnsavedWork(this.element);
    }

    add(event) {
        const scope = event.params.scope;
        const rows = this._rowsFor(scope);
        if (!rows) return;
        this._added += 1;
        const rowId = `new-${this._added}`;

        rows.insertAdjacentHTML('beforeend', this.templateTarget.innerHTML);
        const row = rows.lastElementChild;
        if (!row) return;

        const marker = row.querySelector('input[name="field_row"]');
        if (marker) marker.value = rowId;

        Object.entries(NEW_ROW_INPUTS).forEach(([attribute, field]) => {
            const input = row.querySelector(`[${attribute}]`);
            if (input) input.name = `${field}[${rowId}]`;
        });
        const scopeSelect = row.querySelector('[data-new-scope]');
        if (scopeSelect) scopeSelect.value = scope;

        const label = row.querySelector('input[type="text"]');
        if (label) label.focus();
    }

    // The Recorded setting changed: carry the row to its new section, above
    // that section's retired rows, and show where it went.
    rescope(event) {
        const row = this._row(event);
        const rows = this._rowsFor(event.target.value);
        if (!row || !rows || row.parentElement === rows) return;
        const firstRetired = Array.from(rows.children)
            .find((other) => other.querySelector('[name^="field_retired"]:checked'));
        rows.insertBefore(row, firstRetired || null);
        this._showMoved(row);
    }

    moveUp(event) {
        const row = this._row(event);
        const before = row && row.previousElementSibling;
        if (!before) return;
        this._swap(row, before, () => before.before(row));
    }

    moveDown(event) {
        const row = this._row(event);
        const after = row && row.nextElementSibling;
        if (!after) return;
        this._swap(row, after, () => after.after(row));
    }

    retire(event) {
        // Immediate feedback that the row is on its way out. The row still
        // posts: retiring is a flag on the definition, never a removal.
        const row = this._row(event);
        if (row) row.classList.toggle('opacity-50', event.target.checked);
    }

    close(event) {
        event.preventDefault();
        if (this._dirty && !window.confirm(CLOSE_PROMPT)) return;
        const frame = this.element.closest('turbo-frame');
        if (frame) frame.innerHTML = '';
    }

    _row(event) {
        return event.target.closest('[data-field-row]');
    }

    _rowsFor(scope) {
        return this.rowsTargets.find((rows) => rows.dataset.scope === scope);
    }

    // Any change to the rows: say so once, then redraw the preview.
    _edited() {
        if (!this._dirty) {
            this._dirty = true;
            showSaveStatus(this._marker, 'dirty');
            unsavedWorkChanged();
        }
        clearTimeout(this._previewTimer);
        this._previewTimer = setTimeout(() => this._refreshPreview(), PREVIEW_DELAY_MS);
    }

    // Ask the server for the screens the unsaved rows make. With no connection
    // the previews keep their last drawing; the rows themselves are unaffected.
    _refreshPreview() {
        const form = this.element.querySelector('form');
        if (!form || !this.previewUrlValue || !window.Turbo) return;
        const request = ++this._previewRequest;
        fetch(this.previewUrlValue, {
            method: 'POST',
            body: new FormData(form),
            credentials: 'same-origin',
            headers: { Accept: 'text/vnd.turbo-stream.html' },
        })
            .then((response) => (response.ok ? response.text() : null))
            .then((html) => {
                if (html === null || request !== this._previewRequest || !this.element.isConnected) return;
                window.Turbo.renderStreamMessage(html);
            })
            .catch(() => {});
    }

    // Rows look alike, so a bare node swap reads as nothing happening. The swap
    // is the DOM change (order is DOM order); the animation only shows it: each
    // row is measured, the nodes swap, and each plays from where it was to
    // where it now is. A move still in flight is cancelled first, or the next
    // measurement would include its offset.
    _swap(row, other, move) {
        const rows = [row, other];
        rows.forEach((el) => el.getAnimations()
            .filter((animation) => animation.id === MOVE_ANIMATION.id)
            .forEach((animation) => animation.cancel()));
        const was = rows.map((el) => el.getBoundingClientRect().top);
        move();
        if (!REDUCED_MOTION.matches) {
            rows.forEach((el, i) => {
                const offset = was[i] - el.getBoundingClientRect().top;
                el.animate([{ transform: `translateY(${offset}px)` }, { transform: 'none' }],
                           MOVE_ANIMATION);
            });
        }
        this._showMoved(row);
        this._edited();
    }

    // Keep the moved row on screen and replay its highlight (settings.css),
    // restarting it when the same row moves again before it has finished.
    _showMoved(row) {
        row.scrollIntoView({ block: 'nearest' });
        row.classList.remove('field-row-moved');
        void row.offsetWidth;
        row.classList.add('field-row-moved');
    }
}
